import { type Ref, useState } from 'react'
import cx from 'classnames'
import { useCombobox } from 'downshift'
import { ModalSheet, ModalSheetContent } from '@/components/ModalSheet'
import { ModalSheetSearchHeader } from '@/components/ModalSheet/ModalSheetSearchHeader'
import { TypeBadge } from '@/features/legends/components/TypeBadge'
import { rankedSearch } from '@/features/legends/editor/ranked-search'
import type { RynoType } from '@/features/legends/editor/ryno-types'
import { QuestionIcon } from '@/features/legends/questions/components/QuestionIcon'
import type { QuestionType } from '@/features/legends/questions/question-types'
import styles from './SymbolPalette.module.css'

/**
 * An element the palette can jump to.
 */
export interface SymbolPaletteItem {
  key: string
  id: string
  /**
   * The element's label or question.
   */
  text: string
  /**
   * The paths the element is nested in.
   */
  path: string
  type: QuestionType
  friendlyType: string
  valueType: RynoType | null
}

export interface SymbolPaletteProps {
  isOpen: boolean
  items: readonly SymbolPaletteItem[]
  onSelect: (key: string) => unknown
  onClose: () => unknown
  testId?: string
}

/**
 * Elements are searched by ID first, then by their text.
 */
const getItemKeys = (item: SymbolPaletteItem) => [item.id, item.text]

/**
 * Command palette for jumping to any element of the legend.
 */
export function SymbolPalette({
  isOpen,
  items,
  onSelect,
  onClose,
  testId,
}: SymbolPaletteProps) {
  return (
    <ModalSheet showing={isOpen} onRequestClose={onClose} testId={testId}>
      <SymbolPaletteSearch items={items} onSelect={onSelect} testId={testId} />
    </ModalSheet>
  )
}

interface SymbolPaletteSearchProps {
  items: readonly SymbolPaletteItem[]
  onSelect: (key: string) => unknown
  testId?: string
}

/**
 * The search and its results. The sheet mounts it each time it opens, so
 * every search starts empty.
 */
function SymbolPaletteSearch({
  items,
  onSelect,
  testId,
}: SymbolPaletteSearchProps) {
  const [query, setQuery] = useState('')
  const results = rankedSearch(items, query, getItemKeys)
  const {
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    setHighlightedIndex,
  } = useCombobox({
    isOpen: true,
    items: results,
    itemToKey: (item) => item?.key,
    selectedItem: null,
    defaultHighlightedIndex: 0,
    onSelectedItemChange: ({ selectedItem }) =>
      selectedItem && onSelect(selectedItem.key),
  })

  // The search field reports the text, not the event, so the input's own
  // `onChange` isn't used; Downshift adds the ref at runtime, but its typings
  // omit it.
  const {
    ref: inputRef,
    onKeyDown,
    'aria-activedescendant': activeDescendant,
    'aria-autocomplete': autocomplete,
    'aria-controls': controls,
    'aria-expanded': expanded,
  } = getInputProps({}, suppressRefError) as ReturnType<
    typeof getInputProps
  > & { ref: Ref<HTMLInputElement> }

  return (
    <>
      <ModalSheetSearchHeader
        bordered
        value={query}
        onChange={(value) => {
          setQuery(value)
          setHighlightedIndex(0)
        }}
        onKeyDown={onKeyDown}
        placeholder="Jump to a question, path or variable."
        role="combobox"
        aria-label="Search symbols"
        aria-expanded={expanded}
        aria-controls={controls}
        aria-autocomplete={autocomplete}
        aria-activedescendant={activeDescendant}
        ref={inputRef}
        testId={testId && `${testId}-input`}
      />
      <ModalSheetContent>
        <div
          className={styles.results}
          {...getMenuProps({ 'aria-label': 'Symbols' }, suppressRefError)}
          data-test={testId && `${testId}-results`}
        >
          {results.length === 0 ? (
            <p className={styles.empty}>No matching symbols.</p>
          ) : (
            results.map((item, index) => (
              <div
                key={item.key}
                {...getItemProps({ item, index })}
                className={cx(
                  styles.result,
                  index === highlightedIndex && styles.active,
                )}
                data-test={testId && `${testId}-result-${item.key}`}
              >
                <QuestionIcon type={item.type} size={22} />
                <span className={styles.info}>
                  <span className={styles.title}>
                    {item.id ? (
                      <>
                        <span className={styles.id}>{item.id}</span>
                        {!!item.text && (
                          <span className={styles.text}>{item.text}</span>
                        )}
                      </>
                    ) : item.text ? (
                      // Elements without an ID (effects, prefills) go by their text.
                      <span className={styles.name}>{item.text}</span>
                    ) : (
                      <span className={styles.untitled}>
                        Untitled {item.friendlyType}
                      </span>
                    )}
                  </span>
                  {!!item.path && (
                    <span className={styles.path}>{item.path}</span>
                  )}
                </span>
                <TypeBadge type={item.valueType} />
              </div>
            ))
          )}
        </div>
      </ModalSheetContent>
      <div className={styles.footer}>
        <SymbolPaletteHint keys={['↵']}>Open</SymbolPaletteHint>
        <SymbolPaletteHint keys={['↑', '↓']}>Navigate</SymbolPaletteHint>
        <SymbolPaletteHint keys={['esc']}>Close</SymbolPaletteHint>
      </div>
    </>
  )
}

/**
 * The sheet doesn't render the search while closed, which Downshift would
 * otherwise report as a missing ref.
 */
const suppressRefError = { suppressRefError: true }

interface SymbolPaletteHintProps {
  keys: string[]
  children: string
}

function SymbolPaletteHint({ keys, children }: SymbolPaletteHintProps) {
  return (
    <span className={styles.hint}>
      {keys.map((key) => (
        <kbd key={key} className={styles.kbd}>
          {key}
        </kbd>
      ))}
      {children}
    </span>
  )
}
